import { useMemo } from 'react';
import { Marker, Popup, Polyline } from 'react-leaflet';
import { formatDistanceToNow } from 'date-fns';
import { useRealtimeStore, Location } from '../../store/realtimeStore';
import { useUnitStore } from '../../store/unitStore';
import { useAuthStore } from '../../store/authStore';
import { useCcStore } from '../../store/ccStore';
import { useTargetStore } from '../../store/targetStore';
import PhaseChip from '../PhaseChip';
import { PHASE_META } from '../../types/domain';
import { friendlyPlatoonIcon } from '../../lib/milsym';
import { getPlatoon, ACTIVE_SOS_STATUSES } from './mapUtils';
import { shortCallsign, humanizePhase, OPS_COLORS } from './opsChips';

// Platoon marker. Colored by the platoon's current PHASE. SOS is an INDEPENDENT
// overlay (red blinking ring) layered on top of the phase color, never folded
// into the phase palette.
// MILSYM friendly platoon symbol (APP-6-style). Stale platoons (isOnline=false)
// render as faded "ghost" markers; heading draws a movement vector. A permanent
// chip below carries the short callsign + humanized phase (F-3).
const createPlatoonIcon = (
  callsign: string,
  phaseColorHex: string,
  isOnline: boolean,
  isSOS: boolean,
  heading: number | null | undefined,
  phase: string | null | undefined,
  executingAccent: boolean
) =>
  friendlyPlatoonIcon({
    color: phaseColorHex,
    callsign: callsign || 'Unknown',
    heading,
    sos: isSOS,
    ghost: !isOnline,
    chip: {
      title: shortCallsign(callsign),
      sub: humanizePhase(phase) || undefined,
      accent: executingAccent ? OPS_COLORS.targetExecuting : undefined,
    },
  });

// Platoon marker (one platoon = one device = one marker), colored by phase.
export default function PlatoonMarker({ location, onClick }: { location: Location; onClick: () => void }) {
  const { sosEvents } = useRealtimeStore();
  const { getEffectiveUnitIds } = useUnitStore();
  const { user } = useAuthStore();

  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);

  const filteredSOSEvents = useMemo(() => {
    if (!effectiveUnitIds) return sosEvents;
    return sosEvents.filter((sos) => effectiveUnitIds.includes(sos.unitId || ''));
  }, [sosEvents, effectiveUnitIds]);

  const platoon = getPlatoon(location);
  const platoonId = location.platoonId || platoon?.id;
  const phase = platoon?.currentPhase;
  const isOnline = new Date(location.serverTime).getTime() > Date.now() - 5 * 60 * 1000;

  const hasSOS =
    !!platoon?.sos ||
    filteredSOSEvents.some(
      (sos) =>
        !!platoonId &&
        sos.platoonId === platoonId &&
        !!sos.status &&
        ACTIVE_SOS_STATUSES.includes(sos.status)
    );

  // F-5: a platoon actively executing a target flips to the executing accent —
  // driven by the live execution store OR an EXECUTING target's assignment.
  const activeExecutions = useCcStore((s) => s.activeExecutions);
  const targets = useTargetStore((s) => s.targets);
  const isExecutingTarget =
    !!platoonId &&
    (Object.values(activeExecutions).some((ex) => ex.platoonId === platoonId) ||
      targets.some(
        (t) => t.status === 'EXECUTING' && (t.assignments ?? []).some((a) => a.platoonId === platoonId)
      ));

  const phaseHex = phase ? PHASE_META[phase]?.color ?? '#6b7280' : '#6b7280';
  const color = isExecutingTarget ? '#f59e0b' : phaseHex;
  const icon = createPlatoonIcon(
    platoon?.callsign || 'Unknown',
    color,
    isOnline,
    hasSOS,
    location.bearing,
    phase,
    isExecutingTarget
  );

  return (
    <Marker
      position={[location.latitude, location.longitude]}
      icon={icon}
      zIndexOffset={hasSOS ? 5000 : 1200}
      eventHandlers={{ click: () => onClick() }}
    >
      <Popup>
        <div className="p-1 min-w-[180px]">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-bold text-base">{platoon?.callsign || 'Unknown'}</span>
            {hasSOS && <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded">SOS</span>}
          </div>
          {platoon?.platoonNumber != null && (
            <div className="text-xs text-gray-600">Platoon {platoon.platoonNumber}</div>
          )}
          {platoon?.unit?.code && <div className="text-xs text-gray-600">Unit: {platoon.unit.code}</div>}
          {phase && (
            <div className="mt-1">
              <PhaseChip phase={phase} />
            </div>
          )}
          {platoon?.commanderName && (
            <div className="text-xs text-gray-600 mt-1">Comd: {platoon.commanderName}</div>
          )}
          {platoon?.strength != null && <div className="text-xs text-gray-600">Strength: {platoon.strength}</div>}
          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
            {location.battery != null && <span>🔋 {location.battery}%</span>}
            {location.signal != null && <span>📶 {location.signal}</span>}
          </div>
          <div className="mt-1 text-[10px] text-gray-400">
            {formatDistanceToNow(new Date(location.serverTime), { addSuffix: true })}
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

// Live movement trail (recent points buffered in the realtime store).
export function MovementTrail({ deviceId }: { deviceId: string }) {
  const { locationHistory } = useRealtimeStore();
  const history = locationHistory.get(deviceId);

  if (!history || history.points.length < 2) return null;

  const positions = history.points.map((p) => [p.latitude, p.longitude] as [number, number]);

  return (
    <Polyline
      positions={positions}
      pathOptions={{ color: '#3b82f6', weight: 3, opacity: 0.7, dashArray: '10, 5' }}
    />
  );
}
